import { differenceInDays } from "date-fns";
import React, { useMemo, useRef } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import invariant from "tiny-invariant";
import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import { ProjectFilter, TagFilter } from "@lifeat/shared/task-management/TaskFilterTypes";
import { calculateNewOrderRank } from "@lifeat/shared/task-management/TaskListReorderingUtil";
import {
    isTaskDueToday,
    isTaskOverdue,
    isTaskScheduledOnDate,
} from "@lifeat/shared/task-management/TaskManagementUtil";
import { getDateIsoString } from "@lifeat/shared/util/DateUtil";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { useToastListContext } from "@contexts/ToastListContext";
import MoreActionsIcon from "@icons/Menu-TripleVerticalDots-Icon.svg";
import PlusIcon from "@icons/Plus-Icon.svg";
import { Signal, useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import { logError } from "@service/ConsoleLoggerService";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import { TaskDropEventData } from "../../drag-and-drop/DragAndDropTypes";
import { AssignTaskDateEvent, CreateTaskEvent } from "../AmplitudeTaskManagementEvents";
import { useTaskListContext } from "../TaskListContext";
import { ClientTaskItemType, ClientTaskListType, IdentifiableTask } from "../TaskManagementTypes";
import { closestToTodayStrategy, matchingDateStrategy, useRecurringTasks } from "../useRecurringTasks";
import DroppableTaskSpot from "./DroppableTaskSpot";
import { RecurringTaskItem } from "./RecurringTaskItem";
import TaskListProgressBar from "./TaskListProgressBar";
import { DateColumnProps, Row } from "./TaskListRow";
import { DropdownItem } from "./TaskTagDropdownComponents";

const NonDroppableSpacer = styled.div`
    height: 10px;
`;

export const StyledPlusIcon = styled(PlusIcon)`
    stroke: ${(props) => props.theme.textColorPrimary};
`;

export const ButtonContent = styled.div`
    display: flex;
    gap: 5px;
    width: 100%;
    justify-content: flex-start;
    align-items: center;
`;

const TaskListAndProgressBarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: inherit;
    padding-top: 10px;
`;

const NewTaskButton = styled(LifeAtButtonV2)`
    width: auto;
    margin: 0 20px;
`;

const NEW_TASK_BUTTON_LABEL = "Add task";

const Viewport = styled.div`
    position: relative;
    ${ScrollbarStyles};
    scrollbar-gutter: stable;
    overflow: auto;
    flex: 1;
`;

const ScrollArea = styled.div<{ isTouchDevice: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    min-height: 100%;

    display: flex;
    flex-direction: column;

    padding-right: ${(props) => (props.isTouchDevice ? "10px" : "0px")};
`;

const LastDroppableTaskSpot = styled(DroppableTaskSpot)`
    flex: 1;
    padding: 2px 9px 2px 20px;
`;

const StyledMoreActionsIcon = styled(MoreActionsIcon)`
    fill: ${(props) => props.theme.iconColor};
    cursor: pointer;
`;

const FooterRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 8px;
`;

const StyledMoreActionsDropdownTrigger = styled(DropdownMenuComponents.Trigger)`
    display: flex;
    padding: 3px;
`;

// Used in Array.sort() to sort tasks in descending order of orderRank.
const TASK_ARRAY_SORT_BY_ORDER_RANK = (a: IdentifiableTask, b: IdentifiableTask) => {
    return b.document.orderRank - a.document.orderRank;
};

const DELETE_ALL_TASKS_LABEL = "Delete all tasks";
const DELETE_ALL_COMPLETED_TASKS_LABEL = "Delete all completed tasks";

const RECURRING_DROP_ERROR_TITLE = "Recurring task drag & drop is not supported yet.";

interface TaskListProps {
    /**
     * The user's task data to be used.
     */
    readonly taskList$: Signal<ClientTaskListType>;
    /**
     * Tell amplitude where the event is triggered from
     */
    readonly amplitudeEventSource: AmplitudeEventSource;

    /**
     * If the progress bar should be displayed at the bottom of the task list.
     * By default, it is hidden
     */
    readonly showProgressBar?: boolean;
    /**
     * If provided, the task list is being rendered for a date (in Planner Board View).
     */
    readonly dateColumnProps?: DateColumnProps;
    /**
     * On task move operation, this prop will determine which side effect to perform based on the task's project ID if any project filters are active.
     * "selected-project-filters" (default behavior if prop is not provided) will update the selected project filters to include the moved task's project ID (if it exists)
     * "task-project" will set the task's project ID field to the first project in the list of selected project filters.
     */
    readonly updateTargetOnTaskMove?: "selected-project-filters" | "task-project";
}
export default function TaskList(props: TaskListProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { addToastNotification } = useToastListContext();
    const {
        filters,
        taskFocusEvent$,
        createDraftTask,
        updateTaskScheduleDateAndOrderRank,
        reorderTask,
        toggleTagFilter,
        toggleOverdueFilter,
        toggleUnscheduledFilter,
        toggleOpenStatusFilter,
        toggleTodayFilter,
        clearTagFilters,
        toggleProjectFilter,
        clearProjectFilters,
        updateTaskProject,
        deleteTaskList,
    } = useTaskListContext();
    const dateSelectionStrategy = useMemo(() => {
        if (props.dateColumnProps?.date) {
            return matchingDateStrategy(props.dateColumnProps.date);
        }
        return closestToTodayStrategy();
    }, [props.dateColumnProps?.date?.getTime()]);
    const recurringTasks = useRecurringTasks({
        dateSelectionStrategy,
    });

    const viewportRef = useRef<HTMLDivElement | null>(null);

    const orderedTasks$ = useSignal<IdentifiableTask[]>([]);

    useSignalEffect(() => {
        const tasks: IdentifiableTask[] = [];
        Object.keys(props.taskList$.value).forEach((id: string) => {
            const task = props.taskList$.value[id];
            invariant(task, "Error task in taskList data doesnt exist");
            tasks.push({ id, document: task });
        });

        // Tasks are ordered in descending order of orderRank.
        // A higher orderRank means the task is comes earlier in the list (has a lower array index).
        const sorted = tasks.sort(TASK_ARRAY_SORT_BY_ORDER_RANK);
        orderedTasks$.value = sorted;
    });

    const createDraftTaskAndFocus = (lowerBoundTaskId?: string, upperBoundTaskId?: string) => {
        // if the overdue filter is currently selected, unselect
        // the filter when a new task is added
        if (filters.some((filter) => filter.type === "overdue")) {
            toggleOverdueFilter();
        }

        let lowerBoundOrderRank: number | undefined;
        let upperBoundOrderRank: number | undefined;

        if (lowerBoundTaskId == null && upperBoundTaskId == null) {
            // create task at top of list
            lowerBoundOrderRank =
                props.dateColumnProps?.highestOrderRankTask?.document.orderRank ??
                orderedTasks$.value[0]?.document.orderRank;
            upperBoundOrderRank = undefined;
        } else {
            lowerBoundOrderRank = lowerBoundTaskId ? props.taskList$.value[lowerBoundTaskId]?.orderRank : undefined;
            upperBoundOrderRank = upperBoundTaskId ? props.taskList$.value[upperBoundTaskId]?.orderRank : undefined;
        }

        const orderRank = calculateNewOrderRank(
            {
                lowerBound: lowerBoundOrderRank,
                upperBound: upperBoundOrderRank,
            },
            {
                aboveLowerBound: lowerBoundTaskId ? upperBoundOrderRank : undefined,
                belowUpperBound: upperBoundTaskId ? lowerBoundOrderRank : undefined,
            },
        );

        const draftTaskId = createDraftTask({
            orderRank,
            scheduleDate: props.dateColumnProps?.date,
        });

        taskFocusEvent$.value = {
            type: "singular",
            taskId: draftTaskId,
        };
        amplitudeService.logEvent(new CreateTaskEvent({ source: props.amplitudeEventSource, clientId: draftTaskId }));
        if (props.dateColumnProps?.date) {
            amplitudeService.logEvent(
                new AssignTaskDateEvent({
                    source: props.amplitudeEventSource,
                    method: "directly-on-taskboard",
                    numDaysInFuture: differenceInDays(props.dateColumnProps?.date, new Date()),
                    previousDate: undefined,
                    date: props.dateColumnProps.date.toISOString(),
                }),
            );
        }
    };

    const updateTagFiltersOnTaskMove = (dragTask: ClientTaskItemType) => {
        const tagFilters = filters.filter((filter) => filter.type === "tag") as TagFilter[];

        // Update selected filter based on the task's tag
        if (dragTask.tagId && tagFilters.length > 0) {
            const selectedFiltersIncludesTag = tagFilters.filter((filter) => filter.tag === dragTask.tagId).length > 0;
            if (!selectedFiltersIncludesTag) {
                // If the task's tag isn't a selected filter when it's dropped,
                // then we update the filters to include it
                toggleTagFilter(dragTask.tagId);
            }
        } else if (!dragTask.tagId && tagFilters.length > 0) {
            // If the task does not have an assigned tag, and there are tag filters applied,
            // we unset the filters so the unassigned task displays
            clearTagFilters();
        }
    };

    const updateProjectFiltersOnTaskMove = (dragTask: ClientTaskItemType, taskId: string) => {
        const projectFilters = filters.filter((filter) => filter.type === "project") as ProjectFilter[];
        const filtersIncludesTaskProject =
            projectFilters.filter((filter) => filter.project === dragTask.projectId).length > 0;

        if (projectFilters.length > 0) {
            if (props.updateTargetOnTaskMove === "task-project") {
                // if the task's project isn't included in the project filters, we update the task's project
                if (!dragTask.projectId || !filtersIncludesTaskProject) {
                    invariant(projectFilters[0] != null);
                    updateTaskProject(taskId, projectFilters[0].project);
                }
            } else {
                // Update selected filters based on task's project
                if (dragTask.projectId) {
                    if (!filtersIncludesTaskProject) {
                        // If the task's project isn't a selected filter when it's dropped,
                        // then we update the filters to include it
                        toggleProjectFilter(dragTask.projectId);
                    }
                } else {
                    // If the task does not have an assigned project, and there are project filters applied,
                    // we unset the filters so the unassigned task displays
                    clearProjectFilters();
                }
            }
        }
    };

    const updateFiltersOnTaskMove = (dragTask: ClientTaskItemType) => {
        // if a filter is selected and the dragged task doesn't meet the filter criteria, unselect the filter
        if (filters.some((filter) => filter.type === "today") && !isTaskDueToday(dragTask.schedulingDetails)) {
            toggleTodayFilter();
        }
        if (
            filters.some((filter) => filter.type === "overdue") &&
            !isTaskOverdue(dragTask.schedulingDetails, dragTask.status)
        ) {
            toggleOverdueFilter();
        }
        if (filters.some((filter) => filter.type === "status") && dragTask.status !== "OPEN") {
            toggleOpenStatusFilter();
        }
        if (filters.some((filter) => filter.type === "unscheduled") && dragTask.schedulingDetails) {
            toggleUnscheduledFilter();
        }
    };

    const moveTask = (data: TaskDropEventData, date?: Date) => {
        const { dragItem, upperBoundTask, lowerBoundTask } = data;
        if (dragItem.type === "recurring-task-item") {
            // TODO [LAS-1898]: Handle recurring task drag & drop.
            addToastNotification(RECURRING_DROP_ERROR_TITLE, "");
            return;
        }
        const newTasks = [...orderedTasks$.value];

        const dragTask = props.taskList$.value[dragItem.id] ?? dragItem.data;

        invariant(dragTask != null);

        updateTagFiltersOnTaskMove(dragTask);
        updateProjectFiltersOnTaskMove(dragTask, dragItem.id);
        updateFiltersOnTaskMove(dragTask);

        // Pass in the same task because we always know the lower/upper bound tasks
        // since the array should contain all tasks (unfiltered).
        const belowUpperBoundTask = upperBoundTask != null ? lowerBoundTask : undefined;
        const aboveLowerBoundTask = lowerBoundTask != null ? upperBoundTask : undefined;

        const newOrderRank = calculateNewOrderRank(
            {
                lowerBound: lowerBoundTask?.document?.orderRank,
                upperBound: upperBoundTask?.document?.orderRank,
            },
            {
                aboveLowerBound: aboveLowerBoundTask?.document?.orderRank,
                belowUpperBound: belowUpperBoundTask?.document?.orderRank,
            },
        );

        // Update the orderRank of the dragged task, so that when we set the state again it is sorted correctly.
        dragTask.orderRank = newOrderRank;
        if (date && !isTaskScheduledOnDate(dragTask.schedulingDetails, date)) {
            // Update schedule date for local list state and server
            dragTask.schedulingDetails = {
                scheduleDate: getDateIsoString(date),
                ...dragTask.schedulingDetails,
            };
            if (!props.taskList$.value[dragItem.id]) {
                newTasks.push({ id: dragItem.id, document: dragTask });
            }
            updateTaskScheduleDateAndOrderRank(
                dragItem.id,
                getDateIsoString(date),
                newOrderRank,
                lowerBoundTask?.id,
                upperBoundTask?.id,
            ).catch((e) => {
                logError(`Failed to update task schedule on taskMove. Error: ${e}`);
            });
        } else {
            // If TaskList is not for a specific date, we just reorder the task as normal.
            reorderTask(dragItem.id, newOrderRank, lowerBoundTask?.id, upperBoundTask?.id).catch((e) => {
                logError(`Error updating task order on drag and drop: ${getErrorMessage(e)}`);
            });
        }

        // We need to re-sort because of the new local state assignment of the updated orderRank.
        const orderedNewTasks = newTasks.sort(TASK_ARRAY_SORT_BY_ORDER_RANK);
        orderedTasks$.value = orderedNewTasks;
    };

    const handleDeleteAllTasks = () => {
        const taskIdsToDelete = orderedTasks$.value.map((task) => task.id);
        deleteTaskList(taskIdsToDelete);
    };

    const handleDeleteCompletedTasks = () => {
        const taskIdsToDelete = orderedTasks$.value
            .filter((task) => task.document.status === "COMPLETE")
            .map((task) => task.id);
        deleteTaskList(taskIdsToDelete);
    };

    const taskActionsMenu = (
        <DropdownMenuComponents.Root>
            <StyledMoreActionsDropdownTrigger onClick={(e) => e.stopPropagation()}>
                <StyledMoreActionsIcon />
            </StyledMoreActionsDropdownTrigger>
            <DropdownMenuComponents.Content>
                <DropdownItem onClick={handleDeleteAllTasks}>{DELETE_ALL_TASKS_LABEL}</DropdownItem>
                <DropdownItem onClick={handleDeleteCompletedTasks}>{DELETE_ALL_COMPLETED_TASKS_LABEL}</DropdownItem>
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );

    const lastItem$ = useComputed(() => orderedTasks$.value[orderedTasks$.value.length - 1]);
    return (
        <TaskListAndProgressBarWrapper>
            <NewTaskButton onClick={() => createDraftTaskAndFocus()}>
                <ButtonContent>
                    <StyledPlusIcon />
                    {NEW_TASK_BUTTON_LABEL}
                </ButtonContent>
            </NewTaskButton>
            <Viewport ref={viewportRef}>
                <ScrollArea isTouchDevice={isMobile || isTablet}>
                    {recurringTasks.map((task) => (
                        <React.Fragment key={`recurring-${task.masterId}-${task.index}`}>
                            <NonDroppableSpacer />
                            <RecurringTaskItem
                                onKeyPress={() => {}}
                                isTaskInFocus={
                                    taskFocusEvent$.value?.type === "recurring" &&
                                    taskFocusEvent$.value.masterId === task.masterId &&
                                    taskFocusEvent$.value.index === task.index
                                }
                                amplitudeEventSource={props.amplitudeEventSource}
                                task={task}
                            />
                        </React.Fragment>
                    ))}
                    {orderedTasks$.value.map((task, index) => (
                        <Row
                            key={orderedTasks$.value[index]?.id}
                            index={index}
                            viewportRef={viewportRef}
                            data={{
                                amplitudeEventSource: props.amplitudeEventSource,
                                orderedTasks: orderedTasks$.value,
                                dateColumnProps: props.dateColumnProps,
                                onNewDraftTask: createDraftTaskAndFocus,
                                onTaskDrop: moveTask,
                            }}
                        />
                    ))}
                    <LastDroppableTaskSpot
                        key="last-droppable-task-spot"
                        onDrop={(data: TaskDropEventData) => moveTask(data, props.dateColumnProps?.date)}
                        lowerBoundTask={undefined}
                        upperBoundTask={lastItem$.value}
                        addBuffer={false}
                    />
                </ScrollArea>
            </Viewport>
            <FooterRow>
                {taskActionsMenu}
                <TaskListProgressBar taskList$={props.taskList$} isVisible={!!props.showProgressBar} />
            </FooterRow>
        </TaskListAndProgressBarWrapper>
    );
}
